import { ServiceHandler } from '../../utils/api.utils';
import {
  standardQuerySerializer,
  serializeElasticPageableTokenResponse,
} from '../serializers';
import { claimServiceMappings } from './claim.params-serializer';
import { applyQueryConfig, responseStringTransformer } from '../../utils';
import { claimTasksServiceMappings } from './claim-tasks.params-serializer';
import { lossRunsParamsSerializer } from './loss-runs.params-serializer';
import type { GetClaimsReservesResponse } from './responses/GetClaimsReservesResponse';
import type { GetClaimSummaryResponse } from './responses/GetClaimSummaryResponse';
import { claimsReservesServiceMappings } from './claims-reserves.params-serializer';
import type { VendorCategory } from '../../types/claims/VendorCategory';
import type { CategoryDTO } from '../../console/claims/detail/financials/hooks/types';
import type { ClaimDto } from '../../types/claims/claim.dto';

type ClaimWithTotalIncurred = ClaimDto & {
  totalIncurred?: number;
};

/**
 * A claim as a vendor may see it — the console-side mirror of the server's allow-list.
 *
 * Named rather than reusing the member shape so the narrowing is visible here too: a vendor never
 * receives the policy limit, retention, incurred, our coverage position, the insured's or
 * producer's contact details, or the other vendors engaged on the claim.
 *
 * `accountId` is carried — the invoice document is filed under the claim's account, and a vendor
 * is already told the company name, so it is an opaque key for an entity they can name. The
 * producer's `agencyId` is not, and should not be.
 */
export type VendorClaim = Pick<
  ClaimDto,
  | 'id'
  | 'claimNumber'
  | 'companyName'
  | 'status'
  | 'incidentType'
  | 'reportedDate'
  | 'incidentDate'
  | 'handlerFirstName'
  | 'handlerLastName'
  | 'handlerEmail'
  | 'handlerImageUrl'
  | 'modified'
  | 'accountId'
> & {
  /** Not declared on ClaimDto, though the service returns it on a closed claim. */
  readonly closedDate?: string;
};

export type VendorClaimsResponse = {
  data: VendorClaim[];
  meta: {
    total: number;
    nextToken?: string;
    prevToken?: string;
  };
};

export type MemberClaimsResponse = {
  data: ClaimWithTotalIncurred[];
  meta: {
    total: number;
    nextToken?: string;
    prevToken?: string;
  };
};

const claimsParamSerializer = (params: Record<string, unknown>) => {
  return applyQueryConfig(claimServiceMappings, params);
};

/**
 * The query string for both claims listings.
 *
 * Shared so the vendor listing pages, sorts and filters exactly like the internal one — the two
 * differ in what comes back, not in how they are asked.
 */
export const serializeClaimsQuery = (params: Record<string, unknown>) =>
  standardQuerySerializer(claimsParamSerializer(params));

const claimsReservesParamSerializer = (params: Record<string, unknown>) => {
  return applyQueryConfig(claimsReservesServiceMappings, params);
};

export const getClaims = ServiceHandler<unknown, MemberClaimsResponse>({
  url: '/api/claimsmgmt/v1',
  paramsSerializer: serializeClaimsQuery,
  transformResponse: [
    responseStringTransformer,
    serializeElasticPageableTokenResponse,
  ],
});

/**
 * The claims a vendor is engaged on.
 *
 * Vendor-only, and deliberately not `getClaims` with a different caller. That one answers with the
 * whole claim document: policy limit and retention, total incurred, the insured's revenue, our
 * coverage position, the policyholder's and producer's contact details, and every other vendor
 * engaged on the claim. Which claims came back was already correct; what each record carried was
 * not.
 *
 * This returns an allow-listed projection instead. The response shape is unchanged, so the table
 * plumbing takes it as-is.
 */
export const getVendorClaims = ServiceHandler<unknown, VendorClaimsResponse>({
  url: '/api/claimsmgmt/v1/vendor/claims',
  paramsSerializer: serializeClaimsQuery,
  transformResponse: [
    responseStringTransformer,
    serializeElasticPageableTokenResponse,
  ],
});

export const getClaim = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId',
});

export const getClaimById = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId',
});

export const createClaim = ServiceHandler({
  url: '/api/claimsmgmt/v1',
  method: 'POST',
});

export const updateClaim = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId',
  method: 'PUT',
});

export const deleteClaim = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId',
  method: 'DELETE',
});

export const getClaimIncidentTypes = ServiceHandler({
  url: '/api/claimsmgmt/v1/incidentTypes',
});

export const getClaimReserveTotal = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/reserve/totalV2',
});

export const getClaimInvoiceTotalPaid = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/invoice/totalPaidV2',
});

// This API contains incident types AND secondary incident types
export const getClaimIncidentTypesV2 = ServiceHandler({
  url: '/api/claimsmgmt/v1/incidentTypesV2',
});

export const getClaimIncidentTypesV3 = ServiceHandler({
  url: '/api/claimsmgmt/v1/incidentTypesV3/:productType',
});

export const getLitigationStatuses = ServiceHandler({
  url: 'api/claimsmgmt/v1/litigationStatuses',
});

export const getClaimsHandlers = ServiceHandler({
  url: '/api/auth/v1/users/claim-handlers',
});

export const updateClaimIncidentInfo = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/incidentinfo',
  method: 'put',
});
export const updateClaimsCoverageInfo = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/coverageinfo',
  method: 'PUT',
});

export const updateClaimPatientZeroInfo = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/patientzeroinfo',
  method: 'PUT',
});

export const getClaimTasks = ServiceHandler({
  url: '/api/claimsmgmt/v1/task/claim/:claimId',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(claimTasksServiceMappings, params)
    ),
});

export const getClaimTaskCategories = ServiceHandler({
  url: '/api/claimsmgmt/v1/task/category',
});

export const createClaimTask = ServiceHandler({
  url: '/api/claimsmgmt/v1/task',
  method: 'POST',
});

export const deleteClaimTask = ServiceHandler({
  url: '/api/claimsmgmt/v1/task/:taskId',
  method: 'DELETE',
});

export const updateClaimTask = ServiceHandler({
  url: '/api/claimsmgmt/v1/task/:taskId',
  method: 'PUT',
});

export const getClaimTaskStatuses = ServiceHandler({
  url: '/api/claimsmgmt/v1/task/status',
});

export const getClaimHandlers = ServiceHandler({
  url: '/api/auth/v1/users/claim-handlers',
});

export const updateMostLikelyOutcome = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/mostlikelyoutcome',
  method: 'put',
});

// stats bar
export const getClaimsStatusAggs = ServiceHandler({
  url: '/api/claimsmgmt/v1/aggregate/claims',
  paramsSerializer: (params) =>
    standardQuerySerializer(claimsParamSerializer(params)),
});

export const getClaimsIncurredAggs = ServiceHandler({
  url: '/api/claimsmgmt/v1/aggregate/incurred',
  paramsSerializer: (params) =>
    standardQuerySerializer(claimsParamSerializer(params)),
});

export const getClaimsAggregateReserves = ServiceHandler({
  url: '/api/claimsmgmt/v1/policy/:policyId/reserve/total',
});

type ClaimsFinancialsPolicyInfoDTO = {
  policyAggregateLimit: number;
  policyRetention: number;
  separateCowbellBreachFundCostsLimit?: number;
};

export const getClaimFinancials = ServiceHandler<
  unknown,
  ClaimsFinancialsPolicyInfoDTO
>({
  url: '/api/policy/v1/claims/policyinfo',
});

type ClaimsReservesResp = Record<VendorCategory, number>;
const transformClaimReservesResponse = (data: ClaimsReservesResp) => ({
  firstPartyExpenseReserve: data['First Party Expense'],
  firstPartyLossReserve: data['First Party Loss'],
  lossAdjustmentReserve: data['Loss Adjustment Expense'],
  thirdPartyLossReserve: data['Third-Party Loss'],
  thirdPartyExpenseReserve: data['Third-Party Expense'],
});

export const getClaimReserveCategories = ServiceHandler<
  any,
  ReturnType<typeof transformClaimReservesResponse>
>({
  url: '/api/claimsmgmt/v1/:claimId/reserve/total',
  transformResponse: [
    responseStringTransformer,
    transformClaimReservesResponse,
  ],
});

type PrimaryCategory = Exclude<VendorCategory, 'Incident Response Cost'>;
type PrimaryCategoryBreakdown = Record<string, number | null>;

export type ClaimsReserveBreakdownRespData = {
  Total: number;
  Text: string | null;
  /**
   * FIR-20726: Cyber Crime Loss clawback amount (tied to the Cyber Crime Loss subcategory
   * under First Party Loss). Stored separately from reserves; included here so clients
   * can hydrate the whole Financials tab in one call.
   */
  cyberCrimeFundsRecovered?: number | null;
  /**
   * FPX-7460: per-coverage-line reference limits, keyed by the same adjuster-typed coverage-line
   * name as the reserve breakdown itself. Excess claims only, and display-only — the backend never
   * validates a reserve against them. Absent for products that reserve against a fixed template.
   */
  coverageLimits?: CoverageLimits | null;
  /**
   * FPX-7461: the excess layer's aggregate limit, the claim's running incurred against it (every
   * outstanding reserve line plus everything paid) and what remains. Absent for any claim with no
   * aggregate to track against, which is every non-excess claim.
   *
   * Adding a key here means adding it to `NON_COVERAGE_KEYS` too — the excess editor reads every
   * top-level key it does not recognise as a coverage line the adjuster typed.
   */
  layerAggregateLimit?: number | null;
  incurred?: number | null;
  remaining?: number | null;
} & Record<PrimaryCategory, PrimaryCategoryBreakdown>;

export type CoverageLimits = Record<string, number>;

export const getClaimReserveBreakdown = ServiceHandler<
  any,
  ClaimsReserveBreakdownRespData
>({
  url: '/api/claimsmgmt/v1/:claimId/reserve/totalV2',
});

export const updateClaimFinancials = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/reserve',
  method: 'PUT',
});

/**
 * Reserve write payload: one object per category keyed by its name, each holding that category's
 * subcategory amounts. An excess claim's coverage lines are adjuster-typed rather than drawn from a
 * category tree, and carry their reference limits alongside in {@link CoverageLimits}. Omitting
 * `coverageLimits` leaves any limits already on the claim standing.
 */
export type ClaimReserveBreakdownUpdateData = {
  Text?: string | null;
  coverageLimits?: CoverageLimits;
  /** One entry per category (or, on an excess claim, per adjuster-typed coverage line). */
  [category: string]: Record<string, number | null> | string | null | undefined;
};

/**
 * A write that succeeded, and anything it has to say about the result.
 *
 * FPX-7461: an excess reserve may be taken past the layer's aggregate limit — enforcement is soft by
 * design — so the save is applied and `warning` describes where it leaves the claim. Absent when
 * there is nothing to flag, which is every non-excess write.
 */
export type WriteResult = {
  message: string;
  warning?: string;
};

export const updateClaimFinancialsReserveBreakdown = ServiceHandler<
  ClaimReserveBreakdownUpdateData,
  WriteResult
>({
  url: '/api/claimsmgmt/v1/:claimId/reserveV2',
  method: 'PUT',
});

/**
 * Cyber Crime Loss clawback amount (FIR-20726), tied to the Cyber Crime Loss subcategory
 * under First Party Loss.
 * Read: comes back on {@link getClaimReserveBreakdown} as the top-level
 * {@code cyberCrimeFundsRecovered} field (sibling of Text/Total/categories).
 * Write: dedicated PUT so inline edits do not trigger the reserve-change
 * confirmation modal or create a reserve audit snapshot.
 */
export type CyberCrimeFundsRecoveredResp = {
  fundsRecovered: number | null;
};

export const updateCyberCrimeFundsRecovered = ServiceHandler<
  { fundsRecovered: number | null },
  CyberCrimeFundsRecoveredResp
>({
  url: '/api/claimsmgmt/v1/:claimId/first-party-loss/cyber-crime/funds-recovered',
  method: 'PUT',
});

export type ClaimReservePaidBreakdownRespData = ClaimsReserveBreakdownRespData;

export const getClaimReservePaidBreakdownV2 = ServiceHandler<
  any,
  ClaimReservePaidBreakdownRespData
>({
  url: '/api/claimsmgmt/v1/:claimId/invoice/totalPaidV2',
});

// claim status apis
export const getCloseClaimReasons = ServiceHandler({
  url: '/api/claimsmgmt/v1/closeclaimreasons',
});

export const getDenyClaimReasons = ServiceHandler({
  url: '/api/claimsmgmt/v1/denyClaimReasons',
  method: 'GET',
});

export const closeClaim = ServiceHandler({
  url: '/api/claimsmgmt/v1/close/:claimId',
  method: 'PUT',
});

export const reopenClaim = ServiceHandler({
  url: '/api/claimsmgmt/v1/reopen/:claimId',
  method: 'PUT',
});

/** What regenerating a claim's acknowledgment returns: the draft's msgId, and whether this call made
 *  it (false when a draft already existed). */
export interface ClaimAcknowledgmentDraft {
  readonly msgId: string;
  readonly created: boolean;
}

/** Saves a Claim Acknowledgment draft for an accepted claim, or returns the one already there. Never
 *  sends it. 409 for an FNOL claim. */
export const createClaimAcknowledgmentDraft = ServiceHandler<
  unknown,
  ClaimAcknowledgmentDraft
>({
  url: '/api/claimsmgmt/v1/acknowledgment/:claimId',
  method: 'POST',
});

export const claimNoticeOnlyStatus = ServiceHandler({
  url: '/api/claimsmgmt/v1/noticeonly/:claimId',
  method: 'PUT',
});

// statuses & other dependencies
export const getClaimStatuses = ServiceHandler({
  url: '/api/claimsmgmt/v1/status',
});

export const getClaimSeverities = ServiceHandler({
  url: '/api/claimsmgmt/v1/severity',
});

export const getMyClaimTasks = ServiceHandler({
  url: '/api/claimsmgmt/v1/task/my',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(claimTasksServiceMappings, params)
    ),
});

// loss-runs
export const getLossRunsHistory = ServiceHandler({
  url: '/api/claimsmgmt/v1/search/lossrun',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(lossRunsParamsSerializer, params)),
  transformResponse: [
    responseStringTransformer,
    serializeElasticPageableTokenResponse,
  ],
});

export const downloadLossRunsDocument = ServiceHandler({
  url: '/api/docs/v1/lossrun/:accountId',
});

export const generateLossRunsDocument = ServiceHandler({
  url: '/api/policy/v1/lossrun',
  method: 'POST',
});

export const hasAnyOpenClaims = ServiceHandler<void, boolean>({
  url: '/api/claimsmgmt/v1/has-any-open-claims',
});

type SendLossRunsRequestEmailPayload = {
  accountName: string;
  policyNumber?: string;
  requestorName: string;
  requestorEmail: string;
};

export const sendLossRunsRequestEmail = ServiceHandler<
  SendLossRunsRequestEmailPayload,
  SendLossRunsRequestEmailPayload
>({
  url: '/api/email/v1/emails/AccountLossRunsRequestEmail/send',
  method: 'POST',
});

interface IClaimReserveChangelogResp {
  firstPartyExpenseBreakDown: object;
  firstPartyLossBreakDown: object;
  thirdPartyLossBreakDown: object;
  thirdPartyExpenseBreakDown: object;
  lossAdjustmentExpense: object;
  separateCowbellBreachFundCosts: object;
}

const transformReservesChangelogResp = (data: IClaimReserveChangelogResp) => {
  return {
    ...data,
    'First Party Expense': data.firstPartyExpenseBreakDown,
    'First Party Loss': data.firstPartyLossBreakDown,
    'Third-Party Loss': data.thirdPartyLossBreakDown,
    'Third-Party Expense': data.thirdPartyExpenseBreakDown,
  };
};

export const getClaimReserveChangelog = ServiceHandler({
  url: '/api/claimsmgmt/v1/:claimId/reserve',
  transformResponse: [
    responseStringTransformer,
    transformReservesChangelogResp,
  ],
});

export interface ClaimUpdateCloseDatePayload {
  closeDate: string;
  claimId: string;
}

export const claimUpdateCloseDate = ServiceHandler<
  ClaimUpdateCloseDatePayload,
  unknown
>({
  url: '/api/claimsmgmt/v1/update/close-date',
  method: 'PUT',
});

export interface GetClaimsPaidInvoicesGraphResponse {
  hits: {
    total: {
      value: number;
    };
  };
  aggregations: {
    'filter#time range': {
      doc_count: number;
      'date_histogram#Event distribution by time': {
        buckets: {
          key_as_string: string;
          key: number;
          doc_count: number;
          'sum#amount': {
            value: number;
          };
        }[];
      };
    };
  };
}

export const CLAIMS_PAID_INVOICES_GRAPH = 'CLAIMS_PAID_INVOICES_GRAPH';

export const getClaimsPaidInvoicesGraph = ServiceHandler<
  unknown,
  GetClaimsPaidInvoicesGraphResponse
>({
  url: 'api/claimsmgmt/v1/paid-invoices/graph',
  paramsSerializer: (params) =>
    standardQuerySerializer(claimsParamSerializer(params)),
});

export interface GetClaimsReserveGraphResponse {
  hits: {
    total: {
      value: number;
    };
  };
  aggregations: {
    'filter#time range': {
      doc_count: number;
      'date_histogram#Event distribution by time': {
        buckets: {
          key_as_string: string;
          key: number;
          doc_count: number;
          'sum#LossAdjustmentReserveChange': {
            value: number;
          };
          'sum#TotalReserveChange': {
            value: number;
          };
        }[];
      };
    };
  };
}

export const CLAIMS_RESERVES_GRAPH = 'CLAIMS_RESERVES_GRAPH';

export const getClaimsReserveGraph = ServiceHandler<
  unknown,
  GetClaimsReserveGraphResponse
>({
  url: '/api/claimsmgmt/v1/reserve/graph',
  paramsSerializer: (params) =>
    standardQuerySerializer(claimsParamSerializer(params)),
});

export const getClaimsReserves = ServiceHandler<
  unknown,
  GetClaimsReservesResponse
>({
  url: '/api/claimsmgmt/v1/search/reserves',
  paramsSerializer: (params) =>
    standardQuerySerializer(claimsReservesParamSerializer(params)),
  transformResponse: [
    responseStringTransformer,
    serializeElasticPageableTokenResponse,
  ],
});

export const getClaimsReserveHistory = ServiceHandler({
  url: '/api/claimsmgmt/v1/search/reservesV2',
  paramsSerializer: (params) =>
    standardQuerySerializer(claimsReservesParamSerializer(params)),
});

export const getClaimFinancialFormCategories = ServiceHandler({
  url: '/api/claimsmgmt/v1/allCatAndSubCat/:productType',
});

type GetCategoriesTreeForProductResponse = CategoryDTO[];

export const getCategoriesTreeForProduct = ServiceHandler<
  unknown,
  GetCategoriesTreeForProductResponse
>({
  url: '/api/claimsmgmt/v1/categories/tree/:productType',
});

export const getClaimSummary = ServiceHandler<unknown, GetClaimSummaryResponse>(
  {
    url: '/api/claimsmgmt/v1/:claimId/summary',
  }
);
